import {
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useState,
  useMemo,
} from "react";
import useToggleModal from "hooks/useToggleModal";
import { useUser } from "contexts/UserContext";
import StripeModal from "./components/stripe-modal";
import { AmplitudePageName } from "utils/amplitude";

type ModalProps = Partial<{
  // hit the close button
  onHide: () => void;
  // hit the apply (should call onHide inside component)
  onApply: () => void;
  // hit the cancel (should call onHide inside component)
  onCancel: () => void;
}> & { amplitudePageName: AmplitudePageName };

type Context = {
  openModal: (props: ModalProps) => void;
  canOpen?: boolean;
};

const StripeModalContext = createContext<Context>({
  openModal: () => null,
});

type Props = { children: NonNullable<ReactNode> };

export const StripeModalContextProvider = ({ children }: Props) => {
  const { user } = useUser();
  const canOpen = useMemo(() => !user?.has_stripe_account_connected, [user]);
  const { show, open, close } = useToggleModal(false);
  const [modalProps, setModalProps] = useState<ModalProps | undefined>();

  // close modal, clean step
  const closeModal = useCallback(() => {
    setModalProps(undefined);
    close();
  }, [close]);

  // open modal, with optional step
  const openModal = useCallback(
    (props: ModalProps) => {
      setModalProps({
        ...props,
        onCancel: () => {
          closeModal();
          return props.onCancel && props.onCancel();
        },
        onHide: () => {
          closeModal();
          return props.onHide && props.onHide();
        },
        onApply: () => {
          closeModal();
          return props.onApply && props.onApply();
        },
        amplitudePageName: props.amplitudePageName,
      });
      open();
    },
    [open, closeModal],
  );

  return (
    <>
      {/* connect to stripe */}
      {modalProps && <StripeModal {...modalProps} show={show} />}
      <StripeModalContext.Provider
        value={{
          openModal,
          canOpen,
        }}
      >
        {children}
      </StripeModalContext.Provider>
    </>
  );
};

export const useStripeModal = () => useContext(StripeModalContext);
